// The My army tab: the improvement waiting (if any) to choose, your army as it is now, your record,
// and your recovery code.
import React from 'react';
import type { LeagueView, LeaguePlayer } from '../../leagues/api';
import { playerName } from '../../leagues/api';
import type { Act } from './LeaguePage';
import { RecoveryBox } from './LeagueEntry';
import { ArmyTable } from './ArmyTable';
import { plural } from './parts';
import { currentSquad, draw, choiceName, choiceText } from '../../leagues/improvements';

export default function MyArmyTab({ league: L, me, act }: { league: LeagueView; me: LeaguePlayer; act: Act }) {
  const [pick, setPick] = React.useState<string | null>(null);
  const improving = L.settings.improve;
  const myPending = me.pending ?? 0;
  const mySquad = me.army ? currentSquad(me.army) : null;
  const myN = me.army?.improvements?.length ?? 0;
  const myDraw = myPending && mySquad ? draw(L.seed, me.id, myN, mySquad) : null;
  // "Pip (Militia)", or "Militia 2 of 3" for an un-nicknamed core unit, so you know which one.
  const unitLabel = (i: number) => {
    const u = me.army?.units?.[i];
    if (!u) return `Unit ${i + 1}`;
    if (u.nick) return `${u.nick} (${u.type})`;
    return i >= 1 && i <= 3 ? `${u.type} ${i} of 3 (core)` : `${u.type} (${u.role.toLowerCase()})`;
  };

  return (
    <div style={{ display: 'grid', gap: 20, maxWidth: 900 }}>
      {improving && (
        <section style={{ background: 'linear-gradient(135deg, #3d3628, #34363f)', border: '1px solid #6b5a3a', borderRadius: 16, padding: 20, display: 'grid', gap: 12 }}>
          <p className="site-kicker" style={{ color: 'var(--accent)' }}>Improvements</p>
          {myDraw && mySquad ? (
            <>
              <p style={{ fontSize: 18 }}><strong>{unitLabel(myDraw.unit)}</strong> <span style={{ color: '#c9c7d3' }}>was drawn for an improvement. Choose one of these two. It's permanent{myPending > 1 ? `, and ${plural(myPending - 1, 'more is', 'more are')} waiting after this one` : ''}; your next game can't start until it's saved.</span></p>
              <div role="radiogroup" aria-label="Improvement" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, maxWidth: 640 }}>
                {myDraw.options.map(o => (
                  <button key={o} type="button" role="radio" aria-checked={pick === o} onClick={() => setPick(o)}
                    style={{ textAlign: 'left', display: 'grid', gap: 4, padding: '14px 16px', borderRadius: 12, cursor: 'pointer', color: 'var(--ink)', fontSize: 15, fontFamily: 'inherit',
                      background: pick === o ? 'var(--accent-bg)' : 'var(--bg)', border: `2px solid ${pick === o ? 'var(--accent)' : 'var(--panel-line)'}` }}>
                    <strong style={{ fontSize: 18 }}>{choiceName(o)}</strong><span style={{ color: '#c9c7d3' }}>{choiceText(o, mySquad[myDraw.unit])}</span>
                  </button>
                ))}
              </div>
              <button type="button" className="site-btn site-btn-primary site-btn-big" style={{ justifySelf: 'start' }} disabled={!pick || !myDraw.options.includes(pick)}
                onClick={async () => {
                  if (!pick || !window.confirm(`Give ${unitLabel(myDraw.unit)} ${choiceName(pick)}? This is permanent for the rest of the league.`)) return;
                  // Kept picked if saving failed (the error shows), so trying again is one click.
                  if (await act('improve', { n: myN, unit: myDraw.unit, choice: pick })) setPick(null);
                }}>{pick && myDraw.options.includes(pick) ? `Save ${choiceName(pick)}` : 'Pick one to save'}</button>
            </>
          ) : (
            <p style={{ color: '#c9c7d3' }}>{myN ? `${plural(myN, 'improvement')} so far. ` : ''}After each league game (and any round you sit out) one of your units is drawn and offered two improvements: traits it doesn't have yet, and sometimes +1 Move. You choose one.</p>
          )}
        </section>
      )}
      <section className="site-panel" style={{ display: 'grid', gap: 10 }}>
        <h2 className="site-display" style={{ fontSize: 28, textTransform: 'none' }}>{me.army?.name ?? 'Your army'}</h2>
        {me.army?.builtBy && me.army.builtBy !== me.id && <p style={{ color: '#f2d48f' }}>{playerName(L, me.army.builtBy)} built this army for you.</p>}
        <ArmyTable player={me} />
        {(() => { const s = L.standings.find(x => x.id === me.id); return s ? <p className="site-muted">Record: {s.w} won, {s.l} lost{s.d ? `, ${s.d} drawn` : ''}.</p> : null; })()}
        {!improving && <p className="site-muted site-small">This league's armies stay the same all league.</p>}
      </section>
      <RecoveryBox code={L.me!.recovery} />
    </div>
  );
}
